'use client'
import React, { useEffect, useMemo, useState } from "react"
import { useWatch } from "react-hook-form";
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../@/components/ui/select";
import { Textarea } from "../../@/components/ui/textarea"
import { Plus, Clock, Minus } from "lucide-react"
import { toast } from "sonner"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { ClipLoader } from "react-spinners";
import { useAuth } from "@clerk/clerk-react"
import { Calendar } from "../../@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../../@/components/ui/popover";
import { format, isSameDay, startOfToday, parse, isValid } from "date-fns";
import { cn } from "@/lib/utils"
import { addDays, addWeeks, addMonths } from "date-fns";

const editCohortSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["full-time", "part-time"], { errorMap: () => ({ message: "Type is required" }) }),
  duration_value: z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? undefined : v),
    z.coerce.number().min(1, "Duration is required").optional()
  ),
  duration_unit: z.preprocess(
    (v) => (v === "" || v === null || v === undefined ? undefined : v),
    z.enum(["days", "weeks", "months"], {
      errorMap: () => ({ message: "Unit is required" }),
    }).optional()
  ),
  start_date: z.string().min(1, "Start date is required"),
  end_date: z.string().optional(),
}).superRefine((data, ctx) => {
  if (!data.duration_value) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Duration is required",
      path: ["duration_value"],
    });
  }
  if (!data.duration_unit) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Unit is required",
      path: ["duration_unit"],
    });
  }

  const start = parse(String(data.start_date || ""), "yyyy-MM-dd", new Date());
  const endRaw = String(data.end_date || "").trim();
  const end = endRaw ? parse(endRaw, "yyyy-MM-dd", new Date()) : null;

  if (!isValid(start)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Start date is required", path: ["start_date"] });
    return;
  }

  if (endRaw && (!end || !isValid(end))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Invalid end date", path: ["end_date"] });
    return;
  }

  if (end && start > end) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "End date must be after start date", path: ["end_date"] });
  }
})

// Modern Duration Picker Component
function DurationPicker({ value, unit, onValueChange, onUnitChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [tempUnit, setTempUnit] = useState(unit);

  // Keep field-like controls from changing border color on hover.
  const lockNeutralBorderOnHover = "hover:border-(--neutral-300)";

  const valueNum = Number(value);
  const hasDuration = Number.isFinite(valueNum) && valueNum > 0 && Boolean(unit);

  // Format display text
  const displayText = hasDuration ? `${valueNum} ${unit}` : "Set duration";

  // Quick preset options
  const presets = [
    { label: "1 Week", value: 1, unit: "weeks" },
    { label: "2 Weeks", value: 2, unit: "weeks" },
    { label: "1 Month", value: 1, unit: "months" },
    { label: "3 Months", value: 3, unit: "months" },
  ];

  const handlePresetClick = (preset) => {
    onValueChange(preset.value);
    onUnitChange(preset.unit);
    setTempValue(preset.value);
    setTempUnit(preset.unit);
    setIsOpen(false);
  };

  const handleApply = () => {
    onValueChange(tempValue);
    onUnitChange(tempUnit);
    setIsOpen(false);
  };

  const increment = () => {
    setTempValue(prev => Math.min(prev + 1, 999));
  };

  const decrement = () => {
    setTempValue(prev => Math.max(prev - 1, 1));
  };

  useEffect(() => {
    setTempValue(hasDuration ? valueNum : 1);
    setTempUnit(unit || "days");
  }, [value, unit, isOpen]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-full justify-between text-left font-normal bg-(--surface-base) border-2 border-(--neutral-300)",
            lockNeutralBorderOnHover,
          )}
        >
          <span
            className={cn(
              "flex items-center gap-2",
              hasDuration ? "text-(--neutral-700)" : "text-muted-foreground"
            )}
          >
            <Clock className="w-4 h-4 text-(--neutral-500)" />
            {displayText}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="space-y-1">
            <h4 className="font-medium text-sm">Set Duration</h4>
            <p className="text-xs text-(--neutral-500)">Choose a preset or customize</p>
          </div>

          {/* Quick Presets */}
          <div className="grid grid-cols-2 gap-2">
            {presets.map((preset) => {
              const isSelected = valueNum === preset.value && unit === preset.unit;
              return (
                <Button
                  key={`${preset.value}-${preset.unit}`}
                  type="button"
                  variant="outline"
                  size=""
                  onClick={() => handlePresetClick(preset)}
                  className={cn(
                    "h-9 text-sm border-2 border-(--neutral-300)",
                    isSelected
                      ? "border-(--color-primary) text-(--color-primary) hover:border-(--color-primary)"
                      : lockNeutralBorderOnHover
                  )}
                >
                  {preset.label}
                </Button>
              );
            })}
          </div>

          {/* Custom Duration */}
          <div className="space-y-2">
            <div className="text-xs font-medium text-(--neutral-700)">Custom Duration</div>
            
            {/* Stepper and Value */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 flex-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={cn(
                    "h-9 w-9 shrink-0 border-2 border-(--neutral-300)",
                    lockNeutralBorderOnHover
                  )}
                  onClick={decrement}
                  disabled={tempValue <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                
                <Input
                  type="number"
                  min={1}
                  max={999}
                  value={tempValue}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setTempValue(Math.max(1, Math.min(999, val)));
                  }}
                  className="text-center h-9 border-(--neutral-300)"
                />
                
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={cn(
                    "h-9 w-9 shrink-0 border-2 border-(--neutral-300)",
                    lockNeutralBorderOnHover
                  )}
                  onClick={increment}
                  disabled={tempValue >= 999}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Unit Selector */}
              <Select value={tempUnit} onValueChange={setTempUnit}>
                <SelectTrigger className="w-27.5 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="weeks">Weeks</SelectItem>
                  <SelectItem value="months">Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Apply Button */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size=""
              onClick={() => setIsOpen(false)}
              className={cn("flex-1 border-2 border-(--neutral-300)")}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size=""
              onClick={handleApply}
              className={cn("flex-1")}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function EditCohorts({ cohortId, initialData = {}, onAdd, refetchCohorts, open, onOpenChange }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { getToken } = useAuth()
  const [autoEndDateEnabled, setAutoEndDateEnabled] = useState(false)

  const toDateString = (value) => {
    if (!value) return "";
    if (typeof value === "string") {
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
      return "";
    }
    if (value instanceof Date && !Number.isNaN(value.valueOf())) {
      return format(value, "yyyy-MM-dd");
    }
    return "";
  };

  const initialStartDate = useMemo(
    () =>
      toDateString(
        initialData.start_date ??
          initialData.raw?.start_date ??
          initialData.startDate ??
          initialData.start
      ),
    [initialData]
  );

  const initialEndDate = useMemo(
    () =>
      toDateString(
        initialData.end_date ??
          initialData.raw?.end_date ??
          initialData.endDate ??
          initialData.end
      ),
    [initialData]
  );

  const form = useForm({
    resolver: zodResolver(editCohortSchema),
    defaultValues: {
      name: initialData.name || "",
      type: initialData.type || "",
      duration_value:
        initialData.duration_value ?? initialData.raw?.duration_value ?? "",
      duration_unit:
        initialData.duration_unit ?? initialData.raw?.duration_unit ?? "",
      start_date: initialStartDate,
      end_date: initialEndDate
    }
  })

  useEffect(() => {
    if (open) {
      const nextValues = {
        name: initialData.name || "",
        type: initialData.type || "",
        duration_value:
          initialData.duration_value ?? initialData.raw?.duration_value ?? "",
        duration_unit:
          initialData.duration_unit ?? initialData.raw?.duration_unit ?? "",
        start_date: initialStartDate,
        end_date: initialEndDate,
      };

      setAutoEndDateEnabled(!initialEndDate);

      form.reset(nextValues);
      form.setValue("start_date", nextValues.start_date);
      form.setValue("end_date", nextValues.end_date);

      console.log("Initial Data for Edit Cohort:", initialData);
    }
  }, [open, initialData, form, initialStartDate, initialEndDate])

  const startDate = useWatch({ control: form.control, name: "start_date" });
  const endDate = useWatch({ control: form.control, name: "end_date" });
  const durationValue = useWatch({ control: form.control, name: "duration_value" });
  const durationUnit = useWatch({ control: form.control, name: "duration_unit" });

  const startDateObj = useMemo(() => {
    const raw = String(startDate || "").trim();
    if (!raw) return undefined;
    const d = parse(raw, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [startDate]);

  const endDateObj = useMemo(() => {
    const raw = String(endDate || "").trim();
    if (!raw) return undefined;
    const d = parse(raw, "yyyy-MM-dd", new Date());
    return isValid(d) ? d : undefined;
  }, [endDate]);

  const todayStart = useMemo(() => startOfToday(), []);

  const disablePastExceptSelected = (date, selected) => {
    if (!(date instanceof Date) || Number.isNaN(date.valueOf())) return true;
    if (!(selected instanceof Date) || Number.isNaN(selected.valueOf())) {
      return date < todayStart;
    }
    return date < todayStart && !isSameDay(date, selected);
  };

  useEffect(() => {
    if (!autoEndDateEnabled) return;

    if (startDateObj instanceof Date && !Number.isNaN(startDateObj.valueOf()) && durationValue && durationUnit) {
      let endDate;
      const durationNum = Number(durationValue)
      switch (durationUnit) {
        case "days":
          endDate = addDays(startDateObj, durationNum);
          break;
        case "weeks":
          endDate = addWeeks(startDateObj, durationNum);
          break;
        case "months":
          endDate = addMonths(startDateObj, durationNum);
          break;
        default:
          endDate = null;
      }
      if (endDate) {
        form.setValue("end_date", format(endDate, "yyyy-MM-dd"));
      }
    } else {
      form.setValue("end_date", "");
    }
  }, [autoEndDateEnabled, startDateObj, durationValue, durationUnit, form]);

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true)
      const token = await getToken()
      if (!token) return

      if (!cohortId) {
        toast.error("Cohort ID is required for editing")
        setIsSubmitting(false)
        return
      }

      const payload = {
        name: values.name,
        type: values.type,
        duration_value: values.duration_value,
        duration_unit: values.duration_unit,
        start_date: values.start_date || null,
        end_date: values.end_date || null
      }

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      }

      const editCohortResponse = await axios.put(api_endpoint + `/cohort/update/${cohortId}`, payload, reqConfig)
      const editData = editCohortResponse.data?.cohort ?? editCohortResponse.data
      console.log("Updated Cohort: ", editData)
      toast.success(`Edited cohort "${editData.name}" successfully!`)
      onOpenChange(false)
      form.reset({
        name: editData.name || "",
        type: editData.type || "",
        duration_value: editData.duration_value || values.duration_value || 7,
        duration_unit: editData.duration_unit || values.duration_unit || "days",
        start_date: toDateString(editData.start_date) || values.start_date,
        end_date: toDateString(editData.end_date) || values.end_date,
      })
      if (onAdd) onAdd(editData)
      if (refetchCohorts) refetchCohorts()

    } catch (error) {
      console.error("Failed to edit cohort:", error)
      toast.error("Failed to edit cohort. See console.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    form.reset({
      name: initialData.name || "",
      type: initialData.type || "",
      duration_value:
        initialData.duration_value ?? initialData.raw?.duration_value ?? "",
      duration_unit:
        initialData.duration_unit ?? initialData.raw?.duration_unit ?? "",
      start_date: toDateString(initialData.start_date),
      end_date: toDateString(initialData.end_date)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-auto">
        <DialogHeader className="">
          <DialogTitle className="font-heading text-(--neutral-900)">Edit Cohort</DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">Edit the cohort details.</DialogDescription>
        </DialogHeader>

        <div className="">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="edit-cohort-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-heading text-(--neutral-900)">Cohort Name *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter cohort name" className="border border-(--neutral-300) focus:border-(--neutral-300) focus:ring-0 text-sm" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  className="w-full"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-heading text-(--neutral-900)">Type *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl className="w-full">
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select cohort type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="full-time">Full-time</SelectItem>
                          <SelectItem value="part-time">Part-time</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* MODERN DURATION PICKER - Single click opens popover */}
              <FormField
                control={form.control}
                name="duration_value"
                render={({ field: valueField }) => (
                  <FormField
                    control={form.control}
                    name="duration_unit"
                    render={({ field: unitField }) => (
                      <FormItem>
                        <FormLabel className="font-heading text-(--neutral-900)">Duration *</FormLabel>
                        <FormControl>
                          <DurationPicker
                            value={valueField.value}
                            unit={unitField.value}
                            onValueChange={(val) => {
                              valueField.onChange(val);
                              setAutoEndDateEnabled(true);
                            }}
                            onUnitChange={(unit) => {
                              unitField.onChange(unit);
                              setAutoEndDateEnabled(true);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-heading">Start Date *</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant=""
                            className={cn(
                              "w-full justify-start text-left font-normal bg-(--surface-base) border border-(--neutral-300) text-(--neutral-900)",
                              "hover:border-(--neutral-300) hover:bg-(--surface-base)",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {startDateObj instanceof Date && !Number.isNaN(startDateObj.valueOf())
                              ? format(startDateObj, "PPP")
                              : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={startDateObj}
                            onSelect={(d) => {
                              if (d instanceof Date && !Number.isNaN(d.valueOf())) {
                                field.onChange(format(d, "yyyy-MM-dd"));
                              }
                            }}
                            disablePastDates={false}
                            disabled={(date) => {
                              if (disablePastExceptSelected(date, startDateObj)) return true;
                              if (endDateObj instanceof Date && !Number.isNaN(endDateObj.valueOf())) {
                                return date > endDateObj;
                              }
                              return false;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-heading text-(--neutral-900)">End Date (optional)</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant=""
                            className={cn(
                              "w-full justify-start text-left font-normal bg-(--surface-base) border border-(--neutral-300) text-(--neutral-900)",
                              "hover:border-(--neutral-300) hover:bg-(--surface-base)",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {endDateObj instanceof Date && !Number.isNaN(endDateObj.valueOf())
                              ? format(endDateObj, "PPP")
                              : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={endDateObj}
                            onSelect={(d) => {
                              if (d instanceof Date && !Number.isNaN(d.valueOf())) {
                                field.onChange(format(d, "yyyy-MM-dd"));
                              }
                            }}
                            disablePastDates={false}
                            disabled={(date) => {
                              if (disablePastExceptSelected(date, endDateObj)) return true;
                              if (startDateObj instanceof Date && !Number.isNaN(startDateObj.valueOf())) {
                                return date < startDateObj;
                              }
                              return false;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </form>
          </Form>
        </div>

        <DialogFooter className="">
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogClose>

          <Button
            type="submit"
            variant=""
            form="edit-cohort-form"
            className=""
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default EditCohorts